import clsx from 'clsx'
import {useFormik} from 'formik'
import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {Modal, OverlayTrigger, Tooltip} from 'react-bootstrap'
import Container from 'react-bootstrap/Container'
import {useIntl} from 'react-intl'
import * as Yup from 'yup'
import useCancelToken from '../../../../_gori/hooks/UseCancelToken'
import UseYupValidate from '../../../../_gori/hooks/UseYupValidate'
import {
  Button,
  InputTextFormik,
  SelectFormik,
  ValidationErrorModal,
} from '../../../../_gori/partials/widgets'
import {OrdersConfig} from '../core/_const'
import OrderService from '../core/_requests'
import {convertUnit, KTSVG} from '../../../../_gori/helpers'
import {
  DIMENSION_UNIT,
  DIMENSIONS,
  DIMENSIONS_PLACEHOLDER,
  OPTION_DIMENSION_UNIT,
  OPTION_WEIGHT_UNIT,
  WEIGHT_UNIT,
} from '../../../../_gori/constants/_orders'
import {ParcelItem} from '../components/order-child/PackageType'
import {isEmpty} from 'lodash'
import OrderHelper from '../core/_helpers'

type Props = {
  show: boolean
  handleClose: () => void
  disabled?: boolean
  data: {
    id: number
    preset_id: number
    parcels: ParcelItem[]
  }
  handlePackagesTableEditSuccess: () => void
}

const PackagesTableModal: React.FC<Props> = ({
  show,
  handleClose,
  disabled,
  data,
  handlePackagesTableEditSuccess,
}) => {
  const intl = useIntl()
  const {newCancelToken, isCancel} = useCancelToken()
  const {numberYup, stringYup} = UseYupValidate()
  const [loadingSave, setLoadingSave] = useState<boolean>(false)
  const [validationErrors, setValidationErrors] = useState<any>()
  const [currentDimensionUnit, setCurrentDimensionUnit] = useState<string>(DIMENSION_UNIT.IN)
  const [currentWeightUnit, setCurrentWeightUnit] = useState<string>(WEIGHT_UNIT.OZ)
  const orderHelper = OrderHelper()

  const customSelectInputStyle = useMemo(() => {
    const color = disabled ? '#a1a5b7' : '#0B6D65'
    return {
      styleInput: {
        background: 'transparent',
        width: '80px',
        backgroundImage: 'none',
        borderColor: 'transparent',
        '&:hover': {
          borderColor: 'transparent',
          background: '#F0FDFA',
        },
        padding: 0,
        color,
        textAlign: 'right',
      },
      styleDropdownIndicator: {
        color,
        '&:hover': {
          color,
        },
        padding: 0,
        marginRight: 0,
      },
      customMenuPortal: {
        marginTop: '-14px',
      },
    }
  }, [disabled])

  const validateSchema = Yup.object().shape({
    parcels: Yup.array().of(
      Yup.object().shape(
        OrdersConfig.PARCEL.reduce((initParcel, currentName) => {
          switch (currentName) {
            case 'length':
            case 'width':
            case 'height':
            case 'weight':
            case 'package_count':
              initParcel[currentName] = numberYup.parcel(currentName)
              break
            default:
              initParcel[currentName] = stringYup(50, 'UNIT')
              break
          }
          return initParcel
        }, {})
      )
    ),
  })

  const initData = useMemo(() => {
    if (!data?.parcels) return []
    return data.parcels.map((parcel) => ({
      id: parcel.id,
      length: parcel.length || '',
      width: parcel.width || '',
      weight: parcel.weight || '',
      height: parcel.height || '',
      dimension_unit: parcel.dimension_unit || OPTION_DIMENSION_UNIT[0]?.value,
      weight_unit: parcel.weight_unit || OPTION_WEIGHT_UNIT[0]?.value,
      package_type: parcel.package_type,
      package_count: parcel.package_count,
    }))
  }, [data])

  const formik = useFormik({
    initialValues: {
      parcels: initData,
      dimension_unit: currentDimensionUnit,
      weight_unit: currentWeightUnit,
    },
    enableReinitialize: true,
    validationSchema: validateSchema,
    onSubmit: async (values) => {
      packagesTableEdit(values)
    },
  })

  useEffect(() => {
    if (!data || !data.parcels.length) {
      setCurrentDimensionUnit(DIMENSION_UNIT.IN)
      setCurrentWeightUnit(WEIGHT_UNIT.OZ)
    } else {
      const dimensionUnit = data.parcels[0]?.dimension_unit
      const weightUnit = data.parcels[0]?.weight_unit

      setCurrentDimensionUnit(dimensionUnit)
      setCurrentWeightUnit(weightUnit)

      // Set initial values for formik
      if (dimensionUnit !== currentDimensionUnit) {
        formik.setFieldValue('dimension_unit', dimensionUnit)
      }
      if (weightUnit !== currentWeightUnit) {
        formik.setFieldValue('weight_unit', weightUnit)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const packagesTableEdit = useCallback(
    async (values) => {
      setLoadingSave(true)
      const arePropertiesEqual = (obj1, obj2, properties) => {
        return properties.every((prop) => obj1?.[prop]?.toString() === obj2?.[prop]?.toString())
      }

      const dimensionProperties = ['length', 'width', 'height', 'dimension_unit', 'package_count']
      const weightProperties = ['weight', 'weight_unit']

      const fields = values.parcels.map((parcel, index) => {
        const originalParcel = data.parcels?.[index] || {}

        const checkEqualDimension = arePropertiesEqual(originalParcel, parcel, dimensionProperties)
        const checkEqualWeight = arePropertiesEqual(originalParcel, parcel, weightProperties)

        return {
          ...parcel,
          preset_id: checkEqualDimension && checkEqualWeight ? data.preset_id : null,
          package_id: checkEqualDimension ? originalParcel.package_id : null,
          package_type: checkEqualDimension ? originalParcel.package_type : null,
        }
      })

      try {
        await OrderService.patch(
          {
            type: 'package',
            order_ids: [data.id],
            fields: fields,
            update_multiple_parcels: true,
          },
          {cancelToken: newCancelToken()}
        )

        setLoadingSave(false)
        handleClose()
        handlePackagesTableEditSuccess()
      } catch (error: any) {
        setLoadingSave(false)
        if (isCancel(error)) return
        setValidationErrors(error?.response)
      }
    },
    [
      data.id,
      data.parcels,
      data.preset_id,
      handleClose,
      handlePackagesTableEditSuccess,
      isCancel,
      newCancelToken,
    ]
  )

  const removePackage = (key) => {
    const currentParcels = [...formik.values.parcels]
    const updatedParcels = currentParcels.filter((_, index) => index.toString() !== key)
    formik.setFieldValue('parcels', updatedParcels)
  }

  const onChangeDimensionUnit = (e: any) => {
    const newValue = e.value
    setCurrentDimensionUnit(newValue)
    formik.setFieldValue('dimension_unit', newValue)

    // Set dimension_unit for all parcels
    const currentParcels = [...formik.values.parcels]

    const updatedParcels = currentParcels.map((parcel) => ({
      ...parcel,
      dimension_unit: newValue,
    }))
    formik.setFieldValue('parcels', updatedParcels)

    const parcels = formik.getFieldProps('parcels').value
    const firstParcelKey = Object.keys(parcels)?.[0]
    const {package_type} = parcels[firstParcelKey] || {}
    const {editable, fieldsHidden, packageType} = orderHelper.getPackageDetails(package_type)

    if (editable && !isEmpty(fieldsHidden) && packageType) {
      let fieldsCheck = ['length', 'width', 'height']
      fieldsCheck.forEach((field) => {
        if (fieldsHidden.includes(field)) {
          let baseValue = packageType?.parcel?.[field]
          const updatedValue = convertUnit(
            {unit: DIMENSION_UNIT.IN, value: baseValue},
            {unit: newValue}
          ).toString()
          formik.setFieldValue(`parcels.0.${field}`, updatedValue)
        }
      })
    }
  }

  const onChangeWeightUnit = (e: any) => {
    const newValue = e.value
    formik.setFieldValue('weight_unit', newValue)

    // Set weight_unit for all parcels
    const currentParcels = [...formik.values.parcels]
    const updatedParcels = currentParcels.map((parcel) => ({
      ...parcel,
      weight_unit: newValue,
    }))
    formik.setFieldValue('parcels', updatedParcels)
  }

  return (
    <>
      {validationErrors && (
        <ValidationErrorModal
          handleClose={() => {
            setValidationErrors(undefined)
          }}
          response={validationErrors}
        />
      )}
      <Modal
        id='gori_modal_edit_packages_info'
        tabIndex={-1}
        aria-hidden='true'
        centered
        dialogClassName='modal-size-lg h-auto'
        contentClassName='modal-padding'
        show={show}
        backdrop='static'
        onHide={() => {
          handleClose()
          formik.resetForm()
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title bsPrefix={'fw-bolder fs-1'}>
            {intl.formatMessage({id: 'PACKAGE_INFORMATION'})}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Container
            className={clsx({
              'pe-none': disabled,
            })}
          >
            <div className={'d-flex row package-list bg-light rounded py-3 mx-0'}>
              <div className='d-flex justify-content-center align-items-center mb-1'>
                <div className='col-12 d-flex flex-wrap package-labels'>
                  <div className='col-6 d-flex justify-content-between align-items-center pe-5'>
                    <div className='col-6'>
                      <div className='fs-7 fw-bold label'>
                        {intl.formatMessage({id: 'DIMENSIONS'})}{' '}
                        <span className='fw-light'>{'L*W*H'}</span>
                      </div>
                    </div>
                    <div className='col-3'>
                      <SelectFormik
                        className={clsx('dimension-unit-select', {'pe-none': disabled})}
                        placeholder=''
                        label={''}
                        labelClassName={'text-muted'}
                        options={OPTION_DIMENSION_UNIT}
                        name={`dimension_unit`}
                        formik={formik}
                        disabled={disabled}
                        emptyDefault={false}
                        onChange={(e) => onChangeDimensionUnit(e)}
                        hideIndicatorSeparator
                        isSearchable={false}
                        {...customSelectInputStyle}
                      />
                    </div>
                  </div>
                  <div className='col-6 d-flex justify-content-between align-items-center px-0'>
                    <div className='col-6 d-flex justify-content-between align-items-center pe-2'>
                      <div className='col-6'>
                        <div className='fs-7 fw-bold label'>
                          {intl.formatMessage({id: 'WEIGHT'})}
                        </div>
                      </div>
                      <div className='col-6'>
                        <SelectFormik
                          className={clsx('weight-unit-select', {'pe-none': disabled})}
                          label={''}
                          placeholder=''
                          labelClassName={'text-muted'}
                          options={OPTION_WEIGHT_UNIT}
                          name={`weight_unit`}
                          formik={formik}
                          disabled={disabled}
                          emptyDefault={false}
                          onChange={(e) => onChangeWeightUnit(e)}
                          hideIndicatorSeparator
                          isSearchable={false}
                          {...customSelectInputStyle}
                        />
                      </div>
                    </div>
                    <div className='col-6 ps-3'>
                      <div className='fs-7 fw-bold label d-flex justify-content-start align-items-center'>
                        {intl.formatMessage({id: 'PACKAGE_COUNT'})}
                        <OverlayTrigger
                          overlay={
                            <Tooltip id='tooltip-disabled'>
                              {intl.formatMessage({
                                id: 'PACKAGE_COUNT_TOOLTIP',
                              })}
                            </Tooltip>
                          }
                        >
                          <span className='d-flex justify-content-start align-items-start'>
                            <KTSVG
                              path='/media/gori/orders/info.svg'
                              className='ms-1 mb-1 svg-icon-4 cursor-pointer'
                            />
                          </span>
                        </OverlayTrigger>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {Object.entries(formik.getFieldProps('parcels').value).map(
                ([keyItem, valueItem]: any, index) => {
                  const values = Object.entries(formik.getFieldProps('parcels').value)
                  const disabledDelete = values.length < 2 || disabled
                  const displayExtendParts = values.length === 1 && index === 0
                  const {package_type} = valueItem || {}
                  const {editable, fieldsHidden} = orderHelper.getPackageDetails(package_type)
                  return (
                    <div key={index} className={'package-item my-1'}>
                      <div className='row'>
                        <div className='col-12 d-flex flex-wrap'>
                          <div className='col-6 col sm-6 pe-3 d-flex'>
                            <div
                              className={clsx(
                                'col-1 text-muted fw-normal d-flex justify-content-center align-self-start pt-4',
                                {
                                  'd-none': displayExtendParts,
                                }
                              )}
                            >
                              {index + 1}.
                            </div>
                            <div className={displayExtendParts ? 'col-12' : 'col-11'}>
                              <div className='dimensions-wrap d-flex justify-content-start'>
                                {DIMENSIONS.map((item, index) => {
                                  const isFieldHidden = editable && fieldsHidden?.includes(item)
                                  return (
                                    <div
                                      className={clsx('col-4 col-sm-4 pe-2', {
                                        'd-none': isFieldHidden,
                                      })}
                                      key={index}
                                    >
                                      <div className='position-relative dimension-input-wrap'>
                                        <InputTextFormik
                                          type='number'
                                          min={0}
                                          className={clsx('border-0', {'pe-none': disabled})}
                                          labelClassName={'text-muted'}
                                          label={''}
                                          formik={formik}
                                          name={`parcels.${keyItem}.${item}`}
                                          disabled={disabled}
                                          inputClassName={clsx('dimension-input rounded')}
                                          configAppend={{
                                            name: DIMENSIONS_PLACEHOLDER[item],
                                            position: 'left',
                                          }}
                                        />
                                      </div>
                                    </div>
                                  )
                                })}
                              </div>
                            </div>
                          </div>
                          <div className='col-3 col-sm-3 pe-3'>
                            <InputTextFormik
                              type='number'
                              min={0}
                              className={clsx('', {'pe-none': disabled})}
                              labelClassName={'text-muted'}
                              label={''}
                              formik={formik}
                              name={`parcels.${keyItem}.weight`}
                              disabled={disabled}
                            />
                          </div>

                          <div className='col-3 col-sm-3 ps-2'>
                            <div className='col-12 d-flex justify-content-center align-items-center'>
                              <div className={displayExtendParts ? 'col-12' : 'col-10'}>
                                <InputTextFormik
                                  type='number'
                                  min={0}
                                  className={clsx('border-0', {'pe-none': disabled})}
                                  labelClassName={'text-muted'}
                                  label={''}
                                  formik={formik}
                                  name={`parcels.${keyItem}.package_count`}
                                  disabled={disabled}
                                  isInteger={true}
                                />
                              </div>
                              <div
                                className={clsx('col-2 pt-2 align-self-start', {
                                  'd-none': displayExtendParts,
                                })}
                              >
                                <Button
                                  event={() => removePackage(keyItem)}
                                  className={clsx('text-hover-danger border-0 bg-transparent', {
                                    'pe-none opacity-50': disabledDelete,
                                  })}
                                  disabled={disabledDelete}
                                >
                                  <span className='fs-4 text-gray-600'>
                                    <KTSVG
                                      path='/media/gori/orders/trash.svg'
                                      className={'svg-icon-1'}
                                    />
                                  </span>
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                }
              )}
            </div>
          </Container>
        </Modal.Body>
        <Modal.Footer>
          <div className='d-flex justify-content-end'>
            <Button
              className='btn btn-advanced-secondary me-3'
              label={intl.formatMessage({id: 'CANCEL'})}
              event={() => handleClose()}
              disabled={formik.isSubmitting}
            />
            <Button
              className='btn btn-advanced-primary'
              label={intl.formatMessage({id: 'SAVE'})}
              loadingText={intl.formatMessage({id: 'SAVE'})}
              event={formik.handleSubmit}
              loading={loadingSave}
            />
          </div>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export {PackagesTableModal}
